export interface WheelOpts {
  /** degrees between adjacent cards on the rim */
  step: number;
  /** distance (px) from a card to the wheel hub below it (transform-origin Y) */
  radius: number;
  /** number of cards visible on each side of centre */
  visible: number;
}

export interface CardTransform {
  offset: number;
  angle: number;
  scale: number;
  opacity: number;
  z: number;
  active: boolean;
  hidden: boolean;
}

/** Shortest signed offset from `current` to `index` around a ring of `count`.
 *  `current` may be fractional (while dragging) — the wrap still works. */
export function wrapOffset(index: number, current: number, count: number): number {
  let off = index - current;
  if (off > count / 2) off -= count;
  if (off < -count / 2) off += count;
  return off;
}

// Per-step falloff sampled at |offset| = 0,1,2,3,4. Interpolated continuously so
// the wheel resizes/fades smoothly while dragged (not just at integer stops).
const SCALES = [1.16, 0.92, 0.8, 0.68, 0.58];
const OPACITIES = [1, 0.9, 0.62, 0.4, 0.24];

function sample(table: number[], abs: number): number {
  if (abs <= 0) return table[0];
  if (abs >= table.length - 1) return table[table.length - 1];
  const i = Math.floor(abs);
  const f = abs - i;
  return table[i] + (table[i + 1] - table[i]) * f;
}

/**
 * Geometry for one card. `current` is the centre index and may be fractional
 * during a drag, in which case angle/scale/opacity all interpolate smoothly.
 */
export function cardTransform(
  index: number,
  current: number,
  count: number,
  opts: WheelOpts,
): CardTransform {
  const offset = wrapOffset(index, current, count);
  const abs = Math.abs(offset);
  const active = abs < 0.5;
  if (abs > opts.visible + 0.5) {
    return { offset, angle: offset > 0 ? 60 : -60, scale: SCALES[SCALES.length - 1], opacity: 0, z: 0, active, hidden: true };
  }
  return {
    offset,
    angle: offset * opts.step,
    scale: sample(SCALES, abs),
    opacity: sample(OPACITIES, abs),
    z: Math.round(100 - abs),
    active,
    hidden: false,
  };
}

export function nextIndex(current: number, count: number): number {
  return (Math.round(current) + 1) % count;
}

export function prevIndex(current: number, count: number): number {
  return (Math.round(current) - 1 + count) % count;
}

/** Default tuning approved in brainstorm (~30% of the wheel visible) — the
 *  exact slider values locked in on the 12-hero-wheel-arc mockup: a wide,
 *  gentle arc (large radius, small per-card angle, 4 cards each side). */
export const WHEEL_DEFAULTS: WheelOpts = { step: 12, radius: 780, visible: 4 };
